import PropTypes from "prop-types";
import React from "react";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import PrimaryButton from "web/react/components/buttons/primary-button/primary-button";
import EmailCaptureForm from "web/react/components/email-capture-form/email-capture-form";
import LazyModal from "web/react/components/lazy-modal/lazy-modal";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import requester from "web/script/modules/requester";
import userProfiler from "web/script/modules/userprofiler";
import browser from "web/script/utils/browser";
import history from "web/script/utils/history";
import storage from "web/script/utils/storage";
import url from "web/script/utils/url";
import styles from "./recently-viewed-email-capture.module.css";

export class RecentlyViewedEmailCapture extends React.Component {
    constructor() {
        super();

        this.state = {
            emailAddress: "",
            formErrors: undefined,
            imageUrls: [],
            marketingEmails: false,
            overlayOpen: false,
            privacyPolicy: false,
            productIds: [],
            showDone: false,
        };

        this.appElement = React.createRef();
        this.fetchImageUrls = this.fetchImageUrls.bind(this);
        this.changeEmail = this.changeEmail.bind(this);
        this.triggerClose = this.triggerClose.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onContinueShoppingClick = this.onContinueShoppingClick.bind(this);
        this.handleLegalChange = this.handleLegalChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    componentDidMount() {
        const queryParams = history.getCurrentQueryParams();
        // Don't show recently viewed email capture if:
        //  - user has already seen email capture
        //  - user is not a returning user
        //  - atc_campaign tag is set to "paid-signups" (meaning user landed on FB product overlay)
        //  - user is on the checkout page
        if (
            storage.get("recentlyViewedEmailCaptureShown") === true ||
            userProfiler.getRecentlyViewedEmailCaptureShown() === true ||
            !analytics.isReturningUser() ||
            queryParams.get("atc_campaign") === "paid-signups" ||
            environment.get("pageType") === "checkout"
        ) {
            return null;
        }

        const seenProducts = userProfiler.getSeenProducts();
        if (!seenProducts || seenProducts.length === 0) {
            return null;
        }
        const ids = seenProducts.map((product) => product.productId).splice(0, 3);
        this.setState({ productIds: ids });
        // If viewing on mobile and mobile version is enabled, show the mobile version
        if (browser.getViewport() === browser.VIEWPORTS.MOBILE) {
            return null;
        } else if (this.props.desktopVersionEnabled === true) {
            // Show desktop for tablet and desktop and if desktop version is enabled
            setTimeout(this.fetchImageUrls, 1000, ids);
        }
    }

    async fetchImageUrls(ids) {
        const query = {
            product_ids: ids,
        };
        let jsonData = undefined;
        try {
            jsonData = await requester.get("/api/product/images/", query);
        } catch (e) {
            console.error(e);
            return;
        }
        const validResponse = jsonData && jsonData.success && jsonData.data;
        if (!validResponse) {
            return;
        }
        this.setState({
            imageUrls: jsonData.data.image_urls || [],
            overlayOpen: true,
        });
        analytics.event("signup_recent", "open_signup_form", "open_overlay");
        storage.set("recentlyViewedEmailCaptureShown", true);
    }

    changeEmail(event) {
        this.setState({ emailAddress: event.target.value });
    }

    triggerClose() {
        this.setState({ overlayOpen: false });
    }

    onClose() {
        if (this.state.showDone) {
            analytics.event("signup_recent", "close_signup_form_screen_2", "exit_signup");
        } else {
            analytics.event("signup_recent", "close_signup_form_screen_1", "exit_signup", true); // non interactive
        }
        this.triggerClose();
    }

    onContinueShoppingClick() {
        analytics.event("signup_recent", "click_continue_button", "continue_button");
        this.triggerClose();
    }

    handleLegalChange(privacyPolicyChecked, marketingEmailsChecked) {
        const { privacyPolicy, marketingEmails } = this.state;
        // LegalAgreement component can call this on every render, so we need to check that we don't
        // call setState only when necessary
        if (privacyPolicy !== privacyPolicyChecked || marketingEmails !== marketingEmailsChecked) {
            this.setState({
                privacyPolicy: privacyPolicyChecked,
                marketingEmails: marketingEmailsChecked,
            });
        }
    }

    async onSubmit(event) {
        event.preventDefault();
        const { productIds, emailAddress, marketingEmails, privacyPolicy } = this.state;
        analytics.event("signup_recent", "click_signup_button", "signup_button");
        const productId = productIds.splice(0, 1)[0];
        const body = {
            product_id: productId,
            invisible_pids: productIds.join(","),
            email: emailAddress,
            marketing_emails: marketingEmails,
            privacy_policy: privacyPolicy,
        };

        let jsonData = undefined;
        const queryParams = url
            .toQueryString({
                type: "save_for_later_recent",
                product_id: productId,
                email_capture: "true",
            })
            .toString();
        try {
            jsonData = await requester.post(`/account/register/?${queryParams}`, body);
        } catch (e) {
            console.error(e);
            this.setState({
                formErrors: { other: [gettext("account.login.with_magic_link.error_title")] },
            });
            return;
        }
        if (jsonData && jsonData.success) {
            this.setState({
                showDone: true,
            });
            analytics.event("signup_recent", "show_success_screen", "open_overlay");
        } else {
            const { errors } = jsonData.data;
            this.setState({
                formErrors: errors,
            });
        }
    }

    render() {
        const { cookiePolicyLink, privacyPolicyLink, termsAndConditionsLink } = this.props;
        const { shortPolicy } = this.props;
        const { emailAddress, formErrors, imageUrls, overlayOpen, showDone } = this.state;

        const images = imageUrls.map((imageUrl) => {
            return <img key={imageUrl} className={styles.image} src={imageUrl} />;
        });

        const form = (
            <div>
                <div className={styles.header}>
                    <h3 className={styles.title}>
                        {gettext("account.register.return_lead.recently_viewed_items.step2_title")}
                    </h3>
                    <div className={styles.subtitle}>
                        {gettext(
                            "account.register.return_lead.recently_viewed_items.step2_subtitle"
                        )}
                    </div>
                </div>
                <div className={styles.images}>{images}</div>
                <EmailCaptureForm
                    captureType={"signup_recent"}
                    cookiePolicyLink={cookiePolicyLink}
                    emailAddress={emailAddress}
                    emailLabelText={gettext(
                        "account.register.return_lead.recently_viewed_items.email_label"
                    )}
                    formErrors={formErrors}
                    onEmailChange={this.changeEmail}
                    onLegalChange={this.handleLegalChange}
                    onSubmit={this.onSubmit}
                    privacyPolicyLink={privacyPolicyLink}
                    submitText={gettext(
                        "account.register.return_lead.recently_viewed_items.step2_btn"
                    )}
                    termsAndConditionsLink={termsAndConditionsLink}
                    shortPolicy={shortPolicy}
                />
            </div>
        );

        const doneDiv = (
            <div id="email-capture-done">
                <div className={styles.header}>
                    <SVGIcon name="tag" className={styles.tagIcon} />
                    <h3 className={styles.doneTitle}>
                        {gettext("account.register.return_lead.recently_viewed_items.step3_title")}
                    </h3>
                    <div className={styles.doneSubtitle}>
                        {gettext(
                            "account.register.return_lead.recently_viewed_items.step3_subtitle"
                        )}
                    </div>
                </div>
                <PrimaryButton onClick={this.onContinueShoppingClick} fullWidth>
                    {gettext("account.register.return_lead.recently_viewed_items.step3_btn")}
                </PrimaryButton>
            </div>
        );

        return (
            <div id="email-capture-overlay" ref={this.appElement}>
                <LazyModal
                    appElement={
                        this.props.desktopVersionEnabled ? this.appElement?.current : document.body
                    }
                    isOpen={overlayOpen}
                    onRequestClose={this.onClose}
                    className={styles.modal}
                    overlayClassName={styles.overlay}
                >
                    <CloseButton className={styles.closeButton} onClick={this.onClose} />
                    <div className={styles.captureForm}>
                        {!showDone && form}
                        {showDone && doneDiv}
                    </div>
                </LazyModal>
            </div>
        );
    }
}

export default RecentlyViewedEmailCapture;

RecentlyViewedEmailCapture.propTypes = {
    cookiePolicyLink: PropTypes.string,
    desktopVersionEnabled: PropTypes.bool,
    privacyPolicyLink: PropTypes.string,
    termsAndConditionsLink: PropTypes.string,
    shortPolicy: PropTypes.bool,
};
